import { Conversation, NotificationType } from "@markit/common.types";
import { memo, useCallback, useMemo } from "react";
import { Colors } from "../../../utils/colors";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/store";
import { Icon } from "@iconify/react";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import {
  deleteSingleConversation,
  isUnreadConversation,
  updateChatConversationIndicator,
  updateConversationArchivedStatus,
} from "../../../redux/slices/conversationSlice";
import { showNotificationBanner } from "../../../utils/notificationUtils";

type ConversationItemDropdownSettingsProps = {
  conversationItem: Conversation;
  isOpenDropdown: boolean;
  setIsOpenDropdown: (isOpen: boolean) => void;
  placement: "bottom" | "bottomRight";
  onLocalConversationUpdate?: (conversation: Conversation) => void; // This is to help update the local state of the filtered conversations since there is no snapshot listener for these
};

const ConversationItemDropdownSettings = (
  props: ConversationItemDropdownSettingsProps
) => {
  const {
    conversationItem,
    isOpenDropdown,
    setIsOpenDropdown,
    placement,
    onLocalConversationUpdate,
  } = props;
  const uid = useSelector((state: AppState) => state.account.accountData.uid);
  const dispatch = useDispatch();

  const isUnread = useMemo(
    () => isUnreadConversation(conversationItem.chatIndicator, uid),
    [conversationItem, uid]
  );

  const updateChatIndicatorStatus = useCallback(() => {
    const updatedConversation: Conversation = {
      ...conversationItem,
      chatIndicator: conversationItem.chatIndicator.map((member) =>
        member.uid === uid ? { ...member, indicator: !isUnread } : member
      ),
    };
    onLocalConversationUpdate?.(updatedConversation);

    dispatch(
      updateChatConversationIndicator(conversationItem.uid, uid, !isUnread)
    );
    setIsOpenDropdown(false);
  }, [
    conversationItem,
    onLocalConversationUpdate,
    dispatch,
    uid,
    isUnread,
    setIsOpenDropdown,
  ]);

  const updateArchivedStatus = useCallback(() => {
    const updatedConversation: Conversation = {
      ...conversationItem,
      archived: !conversationItem.archived,
    };
    onLocalConversationUpdate?.(updatedConversation);

    dispatch(updateConversationArchivedStatus(conversationItem));
    showNotificationBanner(
      dispatch,
      conversationItem.archived ? "Unarchived" : "Archived",
      NotificationType.AFFIRMATIVE
    );
    setIsOpenDropdown(false);
  }, [
    dispatch,
    conversationItem,
    setIsOpenDropdown,
    onLocalConversationUpdate,
  ]);

  const deleteConversation = useCallback(() => {
    dispatch(deleteSingleConversation(conversationItem.uid, uid));
    setIsOpenDropdown(false);
  }, [dispatch, conversationItem.uid, uid, setIsOpenDropdown]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, info?: { source: "trigger" | "menu" }) => {
      if (info?.source === "trigger" || nextOpen) {
        setIsOpenDropdown(nextOpen);
      }
    },
    [setIsOpenDropdown]
  );

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const menuItems = [
      {
        title: isUnread ? "Mark as Read" : "Mark as Unread",
        icon: "mdi:circle",
        iconColor: Colors.BLUE5,
        key: "mark_as_unread",
        isDestructive: false,
        onPress: updateChatIndicatorStatus,
      },
      {
        title: conversationItem.archived ? "Unarchive" : "Archive",
        icon: conversationItem.archived
          ? "mdi:archive-arrow-up"
          : "mdi:archive-arrow-down",
        iconColor: Colors.BLACK,
        key: "archive",
        isDestructive: false,
        onPress: updateArchivedStatus,
      },
      // {
      //   title: "Delete",
      //   icon: "ion:trash",
      //   iconColor: Colors.RED3,
      //   key: "delete_conversation",
      //   isDestructive: true,
      //   onPress: () => {
      //     const confirmed = window.confirm(
      //       "Are you sure you want to delete this conversation? This cannot be undone."
      //     );
      //     if (confirmed) {
      //       deleteConversation();
      //     }
      //   },
      // },
    ];
    return menuItems;
  }, [
    conversationItem.archived,
    isUnread,
    updateArchivedStatus,
    updateChatIndicatorStatus,
  ]);

  return (
    <DropdownMenu
      dropdownItems={dropdownItems}
      trigger={["click"]}
      placement={placement}
      isOpen={isOpenDropdown}
      onOpenChange={handleOpenChange}
      overlayStyle={{ width: 200 }}
    >
      <div
        style={{
          height: 30,
          width: 30,
          borderRadius: 100,
          backgroundColor: Colors.WHITE1,
          border: `0.5px solid ${Colors.GRAY2}`,
          transition: "opacity 0.2s ease",
        }}
        className="Centering"
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        <Icon
          icon={"mdi:dots-horizontal"}
          height={22}
          style={{ color: Colors.BLACK }}
        />
      </div>
    </DropdownMenu>
  );
};

export default memo(ConversationItemDropdownSettings);
